// @flow
import remove from 'lodash/remove';

import { getSearchValue } from '../utils/http';
import { isDevMode } from '../utils/user';
import type { Platform } from '../utils/platform';
import { SMALL } from '../utils';

type AuthenticationError = {
  name: string,
  message: string,
};

type Action =
  | {
      type: 'FETCH_SCRIBE_SESSION_SUCCESS',
      url: string,
      captioner: ScribeCaptioner,
      support: Array<string>,
    }
  | {
      type: 'ROOM_STATUS_UPDATE',
      status: Object,
    }
  | {
      type: 'GET_BRANCH_IO_DATA',
      content: any,
    }
  | {
      type: 'FETCH_AVA_SESSION_SUCCESS',
      url: string,
      captioner: ScribeCaptioner,
      avaId: string,
      roomId: string,
      support: Array<string>,
      connType: string,
    }
  | {
      type: 'FETCH_AVA_PROFILE_METRICS',
      metrics: Object,
    }
  | {
      type: 'FETCH_FEATURE_FLAG_SUCCESS',
      features: Object,
      subscription: Object,
    }
  | {
      type: 'LOADING_APP',
      status: boolean,
    }
  | {
      type: 'SWITCH_PLATFORM',
      currentPlatform: Platform,
    }
  | {
      type: 'FETCH_FEATURE_FLAG_ERROR',
      error: AuthenticationError,
    }
  | {
      type: 'FETCH_FEATURE_FLAG_REJECT',
    }
  | { type: 'FETCH_SCRIBE_SESSION_ERROR', error: AuthenticationError }
  | { type: 'CREATE_NEW_CONVERSATION', inConferenceCall: Boolean }
  | { type: 'START_CONVERSATIONS_SESSION' }
  | { type: 'USER_UPDATE', user: Object }
  | { type: 'TOGGLE_AUDIO_MODE' }
  | { type: 'ENABLE_AUDIO_MODE' }
  | { type: 'ASYNC_BACKEND_ENDPOINT', status: string, endpoints?: Object }
  | { type: 'TOGGLE_DEV_MODE' }
  | { type: 'ESTABLISHED_SCRIBE_SESSION_CONNECTION' }
  | { type: 'REMOVE_MULTI_EDIT_WEB' }
  | { type: 'ACTIVATE_MULTI_EDIT_WEB' }
  | { type: 'CLEAR_SCRIBE_SESSION' }
  | { type: 'FETCH_AVA_SESSION_ERROR', error: AuthenticationError }
  | { type: 'LOST_SCRIBE_SESSION_CONNECTION' }
  | { type: 'TOGGLE_SIDE_BAR', value?: boolean }
  | { type: 'LOST_WEB_SESSION_CONNECTION' }
  | { type: 'START_SCRIBE_DASHBOARD_SESSION' }
  | { type: 'START_MIRROR_SESSION' }
  | { type: 'START_V1_SESSION' }
  | { type: 'CONNECTION_SUCCEEDED' }
  | { type: 'FETCH_BACKEND_SUPPORT', support: Array<string> }
  | { type: 'STOP_SCRIBE_DASHBOARD_SESSION' }
  | { type: 'STOP_SCRIBE_DASHBOARD_SESSION' }
  | { type: 'CLOSE_SCRIBE_SESSION_WINDOW' }
  | { type: 'UPDATE_IS_IN_CONVERSATION', value: boolean };

type State = {
  +activeFeatures: Array<string>,
  +avaId: string,
  +captioner?: ScribeCaptioner,
  +currentPlatform: string,
  +error?: AuthenticationError,
  +isInConversation?: boolean,
  +features: FeatureMap,
  +subscription: Subscription,
  +loading?: boolean,
  +roomId?: string,
  +socketConnectionStatus: 'PENDING' | 'CONNECTED' | 'DISCONNECTED',
  +url?: string,
  branch: any,
  branchId: string,
  readonly: boolean,
  scribeDashboard?: boolean,
  mirror?: boolean,
  v1?: boolean,
  sidebar: boolean,
  backendSupport: Array<string>,
  intercomDisplayed?: boolean,
  userNameFromUrl?: string,
  lightView: boolean,
  displayable: {
    mirror: boolean,
    v1: boolean,
    scribeDashboard: boolean,
    dev: boolean,
    audio: boolean,
    rv: boolean,
    conversations: boolean,
  },
  backends: Object,
  asyncBackends: string,
};

const initialState = {
  activeFeatures: ['multi_edit_web'],
  loading: true,
  inConferenceCall: false,
  currentPlatform: 'web',
  features: {},
  subscription: {},
  avaId: '',
  socketConnectionStatus: 'PENDING',
  roomId: undefined,
  error: {
    name: '',
    message: '',
  },
  readonly: getSearchValue(window, 'readonly') !== 'false',
  branch: {},
  branchId: localStorage.getItem('ava_branch_id') || '',
  scribeDashboard: undefined,
  lightView: window.location.href.includes('fii.ava.me/session-'),
  mirror: undefined,
  v1: undefined,
  conversations: window.location.href.includes('/web/conversations'),
  sidebar: false,
  backendSupport: [],
  intercomDisplayed: window.innerWidth > 800,
  displayable: {
    mirror: false,
    v1: false,
    scribeDashboard: false,
    dev: window.location.hostname === 'localhost',
    audio: true,
    conversations: window.location.hostname === 'localhost',
    audioDisplay:
      window.location.hostname === 'localhost' ||
      window.location.hostname.includes('serveo') ||
      window.location.hostname.includes('avascribe'),
    rv: false,
  },
  userNameFromUrl: getSearchValue(window, 'guestUserName', undefined),
  backends: {
    production: {
      url: 'https://backend.ava.me',
    },
    playground: {
      url: 'https://playground.ava.me',
    },
    local: {
      url: 'http://localhost:3000',
    },
  },
  asyncBackends: 'none',
};

const updateIntercomDisplay = (state: State) => ({
  ...state,
  intercomDisplayed:
    state.currentPlatform === 'web' && !state.features.scribe && !SMALL && !state.isInConversation,
});

const updateDisplayable = (state: State) => ({
  ...state,
  displayable: {
    ...state.displayable,
    dev:
      state.displayable.dev ||
      isDevMode(state.currentPlatform, state.features, state.backends),
    scribeDashboard: state.features.scribe && state.currentPlatform === 'web',
    v1: state.backendSupport.includes('connectRequest'),
    // eslint-disable-next-line
    mirror: state.captioner
      ? state.captioner.userPermissions
        ? !state.captioner.userPermissions.includes('web-temp-user')
        : false
      : false,
    // eslint-disable-next-line
    rv: state.captioner
      ? state.captioner.userPermissions
        ? state.captioner.userPermissions.includes('scribe-roger-voice')
        : false
      : false,
    conversations:
      state.displayable.conversations ||
      isDevMode(state.currentPlatform, state.features, state.backends),
  },
});

localStorage.removeItem('ava_branch_id');

if (localStorage.getItem('avaMultiEdit') === 'true') {
  initialState.activeFeatures.push('multi_edit');
  initialState.activeFeatures.push('multi_edit_web');
}

export default function scribeConversation(
  state: State = initialState,
  action: Action
): State {
  switch (action.type) {
    case 'FETCH_BACKEND_SUPPORT': {
      const { support } = action;

      return updateDisplayable({
        ...state,
        backendSupport: state.backendSupport.concat(support || []),
      });
    }
    case 'FETCH_SCRIBE_SESSION_ERROR': {
      return { ...state, error: action.error };
    }

    case 'LOADING_APP': {
      return { ...state, loading: action.status };
    }

    case 'FETCH_SCRIBE_SESSION_SUCCESS': {
      const { url, support, captioner } = action;

      return {
        ...state,
        url,
        captioner,
        socketConnected: 'PENDING',
        error: {
          name: '',
          message: '',
        },
        support: state.backendSupport.concat(support || []),
      };
    }

    case 'SWITCH_PLATFORM':
      return updateIntercomDisplay({
        ...state,
        currentPlatform: action.currentPlatform,
      });
    
    case 'UPDATE_IS_IN_CONVERSATION':
      return updateIntercomDisplay({
        ...state,
        isInConversation: action.value,
      });

    case 'ESTABLISHED_SCRIBE_SESSION_CONNECTION':
      return {
        ...state,
        socketConnected: 'CONNECTED',
      };

    case 'LOST_WEB_SESSION_CONNECTION': {
      return {
        ...state,
        socketConnected: 'DISCONNECTED',
      };
    }

    case 'LOST_SCRIBE_SESSION_CONNECTION': {
      return {
        ...state,
        socketConnected: 'DISCONNECTED',
      };
    }

    case 'CLOSE_SCRIBE_SESSION_WINDOW': {
      return {
        ...state,
        url: '',
        socketConnected: 'CLOSED',
      };
    }

    case 'CLEAR_SCRIBE_SESSION': {
      return initialState;
    }

    case 'FETCH_FEATURE_FLAG_SUCCESS': {
      const { features, subscription } = action;
      return updateDisplayable(
        updateIntercomDisplay({
          ...state,
          features,
          subscription,
        })
      );
    }
    case 'FETCH_FEATURE_FLAG_REJECT': {
      return { ...state };
    }

    case 'TOGGLE_DEV_MODE': {
      return {
        ...state,
        displayable: { ...state.displayable, dev: !state.displayable.dev },
      };
    }

    case 'CONNECTION_SUCCEEDED': {
      return {
        ...state,
        socketConnected: 'CONNECTED',
      };
    }

    case 'TOGGLE_AUDIO_MODE': {
      return {
        ...state,
        displayable: { ...state.displayable, audio: !state.displayable.audio },
      };
    }

    case 'ENABLE_AUDIO_MODE': {
      return {
        ...state,
        displayable: { ...state.displayable, audio: true },
      };
    }
    case 'FETCH_AVA_SESSION_SUCCESS': {
      const { url, captioner, avaId, roomId, support } = action;

      return updateDisplayable({
        ...state,
        socketConnected: 'CONNECTED',
        url,
        avaId,
        captioner: {
          ...captioner,
          ...(captioner.profile && { profile: captioner.profile }),
          id: captioner.firebaseAuthUID || action.captioner.id,
        },
        roomId,
        backendSupport: state.backendSupport.concat(support || []),
        mirror: action.connType === 'mirror',
      });
    }

    case 'FETCH_AVA_PROFILE_METRICS': {
      const { metrics } = action;

      return updateDisplayable({
        ...state,

        captioner: {
          ...state.captioner,
          profile: {
            ...(state.captioner && state.captioner.profile),
            feedbackMetrics: metrics,
          },
        },
      });
    }

    case 'CREATE_NEW_CONVERSATION': {
      return {
        ...state,
        loading: true,
        inConferenceCall: action.inConferenceCall,
      };
    }

    case 'FETCH_FEATURE_FLAG_ERROR': {
      return { ...state, error: action.error };
    }
    case 'FETCH_AVA_SESSION_ERROR': {
      return { ...state, error: action.error };
    }
    case 'ACTIVATE_MULTI_EDIT_WEB': {
      return {
        ...state,
        activeFeatures: ['multi_edit_web'].concat(state.activeFeatures),
      };
    }
    case 'REMOVE_MULTI_EDIT_WEB': {
      const active = state.activeFeatures;
      remove(active, feat => feat === 'multi_edit_web');
      return {
        ...state,
        activeFeatures: [],
      };
    }
    case 'START_SCRIBE_DASHBOARD_SESSION': {
      return {
        ...state,
        scribeDashboard: true,
        mirror: false,
        v1: false,
        conversations: false,
      };
    }
    case 'START_CONVERSATIONS_SESSION': {
      return {
        ...state,
        scribeDashboard: false,
        mirror: false,
        v1: false,
        conversations: true,
      };
    }
    case 'START_MIRROR_SESSION': {
      return {
        ...state,
        scribeDashboard: false,
        mirror: true,
        v1: false,
        conversations: false,
      };
    }
    case 'START_V1_SESSION': {
      return {
        ...state,
        scribeDashboard: false,
        mirror: false,
        v1: true,
        conversations: false,
      };
    }
    case 'STOP_SCRIBE_DASHBOARD_SESSION': {
      return {
        ...state,
        scribeDashboard: false,
      };
    }
    case 'TOGGLE_SIDE_BAR': {
      if (state.sidebar === action.value) {
        return state;
      }
      return {
        ...state,
        sidebar: !state.sidebar,
      };
    }
    case 'GET_BRANCH_IO_DATA': {
      return {
        ...state,
        branch: action.content,
      };
    }
    case 'USER_UPDATE': {
      return {
        ...state,
        captioner: {
          ...state.captioner,
          ...action.user,
        },
      };
    }

    case 'ROOM_STATUS_UPDATE': {
      return {
        ...state,
        mirror: !(
          state.currentPlatform === 'web' && action.status.participants
        ),
      };
    }

    case 'ASYNC_BACKEND_ENDPOINT': {
      return {
        ...state,
        asyncBackends: action.status,
        backends: {
          ...state.backends,
          ...(action.endpoints
            ? action.endpoints.reduce((p, c) => ({ ...p, [c.url]: c }), {})
            : {}),
        },
      };
    }

    default:
      // eslint-disable-next-line no-unused-expressions
      (action: empty);
      return state;
  }
}
